/**
 * PSD vector mask → editor `VectorPath`.
 *
 * Tier 2 Group F natives follow-up (item 2a-iv) — closes the
 * deferred "PSD vector path native mapping" from the PSD round-
 * trip plan. Imports ag-psd's `LayerVectorMask` (parsed as a list
 * of `BezierPath` objects) into the editor's `VectorPath` shape so
 * shape / pen-tool layers from Photoshop appear as editable
 * vectors in the editor instead of rasterizing.
 *
 * ## ag-psd → editor mapping
 *
 * **BezierKnot.points = `[x0, y0, x1, y1, x2, y2]`** — absolute
 * coordinates in document pixels:
 *   - (x0, y0) → incoming control point (preceding handle)
 *   - (x1, y1) → anchor point itself
 *   - (x2, y2) → outgoing control point (following handle)
 *
 * The editor's `VectorAnchor` carries `inX/inY` and `outX/outY` as
 * RELATIVE deltas off the anchor, not absolute coords — we subtract
 * `(x1, y1)` from the control points on import.
 *
 * **BezierKnot.linked**: PSD's "linked" flag means the two control
 * points are mirrored (continuous tangent). Maps to the editor's
 * `'smooth'` anchor type. Unlinked knots with both handles present
 * map to `'asymmetric'`; knots with no handles (control coincident
 * with anchor) map to `'corner'`.
 *
 * **BezierPath.open**: open path → editor `VectorSubPath { closed:
 * false }`. Closed path → `closed: true`.
 *
 * **Multiple paths in a single vector mask** (BezierPath[]):
 * concatenated into the editor's `VectorPath.subPaths` list. PSD's
 * boolean operations (`exclude` / `subtract` / `intersect`) aren't
 * resolved in v1 — every sub-path is treated as `'combine'` and
 * the editor's `even-odd` / `non-zero` fillRule handles the visual.
 * For correct rendering of subtractive paths, the stash-restore
 * path preserves the original PSD vectorMask chunk.
 *
 * **VectorContent fill**: only `type: 'color'` is mapped natively
 * (extracted to `VectorPath.fill` as a hex string). Gradient /
 * pattern fills aren't mapped — the importer returns `null` from
 * `parsePsdVectorFill` so the caller falls back to raster.
 *
 * Pure module — no DOM dependencies. Tests inject synthetic
 * ag-psd `Layer` shapes.
 */

import type { Layer as PsdLayer, BezierPath, BezierKnot, Color } from 'ag-psd';
import type { AnchorType, VectorAnchor, VectorPath, VectorStroke, VectorSubPath } from '../types';
import { psdColorToHex } from './psd-color-utils';

/** Reasons a PSD vector layer can't be expressed natively. Caller
 *  routes the failure to the existing raster fallback. */
export type VectorImportRejectReason =
  | 'no-vector-mask'
  | 'gradient-fill'
  | 'pattern-fill'
  | 'unsupported-stroke'
  | 'empty-paths';

export type VectorImportResult =
  | { ok: true; value: VectorPath }
  | { ok: false; reason: VectorImportRejectReason };

/** Tolerance for detecting a "control point coincident with
 *  anchor" — when both control points are within this many pixels
 *  of the anchor, we drop the handles and mark the anchor as a
 *  corner. Sub-pixel drift in the fixed-point encoding produces
 *  near-zero handles that should still be treated as straight
 *  segments. */
const HANDLE_COLLAPSE_EPSILON = 0.001;

/** Parse the PSD layer's vector chunks into a native `VectorPath`.
 *  Returns a rejection reason when the layer's fill or stroke
 *  carries features the editor's model can't represent. */
export function parsePsdVector(psdLayer: PsdLayer): VectorImportResult {
  if (!psdLayer.vectorMask) return { ok: false, reason: 'no-vector-mask' };
  const paths = psdLayer.vectorMask.paths;
  if (!paths || paths.length === 0) return { ok: false, reason: 'empty-paths' };

  const subPaths: VectorSubPath[] = [];
  for (const bezier of paths) {
    const subPath = bezierPathToSubPath(bezier);
    if (subPath.anchors.length > 0) subPaths.push(subPath);
  }
  if (subPaths.length === 0) return { ok: false, reason: 'empty-paths' };

  // Fill: only `type: 'color'` maps natively. Gradient / pattern
  // refs require richer ShapeFillValue / texture support that the
  // editor's VectorPath model doesn't carry.
  const fillResult = parsePsdVectorFill(psdLayer.vectorFill);
  if (fillResult.kind === 'reject') return { ok: false, reason: fillResult.reason };

  // Stroke: solid color + width. PSD's lineAlignment (`inside` /
  // `center` / `outside`) doesn't have a direct editor analog on
  // VectorStroke — defaults to centered rendering.
  const strokeResult = parsePsdVectorStroke(psdLayer);
  if (strokeResult.kind === 'reject') return { ok: false, reason: strokeResult.reason };

  // fillRule: ag-psd reports per-BezierPath; we take the first
  // path's rule since the editor's model is per-VectorPath, not
  // per-sub-path. Mixed fill rules across sub-paths is rare
  // enough to defer.
  const path: VectorPath = {
    subPaths,
    ...(fillResult.kind === 'ok' && fillResult.color !== undefined
      ? { fill: fillResult.color }
      : {}),
    ...(strokeResult.kind === 'ok' && strokeResult.stroke ? { stroke: strokeResult.stroke } : {}),
  };
  return { ok: true, value: path };
}

/** Convert one ag-psd BezierPath into an editor VectorSubPath.
 *  Each knot becomes an editor VectorAnchor with relative handles. */
function bezierPathToSubPath(bezier: BezierPath): VectorSubPath {
  const anchors: VectorAnchor[] = bezier.knots.map((knot) => bezierKnotToAnchor(knot));
  return {
    closed: !bezier.open,
    anchors,
  };
}

/** Convert one ag-psd BezierKnot (absolute coords + linked flag)
 *  into an editor VectorAnchor (relative handles + type tag). */
function bezierKnotToAnchor(knot: BezierKnot): VectorAnchor {
  // points = [x0, y0, x1, y1, x2, y2]
  // - (x1, y1) is the anchor itself
  // - (x0, y0) is the incoming handle (preceding control point)
  // - (x2, y2) is the outgoing handle (following control point)
  const [x0, y0, x1, y1, x2, y2] = knot.points;
  const anchor: VectorAnchor = { x: x1, y: y1, type: 'corner' };
  const inDx = x0 - x1;
  const inDy = y0 - y1;
  const outDx = x2 - x1;
  const outDy = y2 - y1;
  const hasIn =
    Math.abs(inDx) > HANDLE_COLLAPSE_EPSILON || Math.abs(inDy) > HANDLE_COLLAPSE_EPSILON;
  const hasOut =
    Math.abs(outDx) > HANDLE_COLLAPSE_EPSILON || Math.abs(outDy) > HANDLE_COLLAPSE_EPSILON;
  if (hasIn) {
    anchor.inX = inDx;
    anchor.inY = inDy;
  }
  if (hasOut) {
    anchor.outX = outDx;
    anchor.outY = outDy;
  }
  const type: AnchorType = resolveAnchorType(knot.linked, hasIn, hasOut, inDx, inDy, outDx, outDy);
  anchor.type = type;
  return anchor;
}

function resolveAnchorType(
  linked: boolean,
  hasIn: boolean,
  hasOut: boolean,
  inDx: number,
  inDy: number,
  outDx: number,
  outDy: number,
): AnchorType {
  if (!hasIn && !hasOut) return 'corner';
  if (linked) return 'smooth';
  // Unlinked but both handles present — check if they're
  // antiparallel (collinear, opposite directions, possibly
  // different magnitudes). If yes, 'smooth' is the right type
  // (continuous tangent); if not, 'asymmetric'.
  if (hasIn && hasOut) {
    // Cross product near zero → collinear; sign of dot product
    // tells us if they're parallel (corner) or antiparallel (smooth).
    const cross = inDx * outDy - inDy * outDx;
    if (Math.abs(cross) < HANDLE_COLLAPSE_EPSILON) {
      const dot = inDx * outDx + inDy * outDy;
      return dot < 0 ? 'smooth' : 'corner';
    }
    return 'asymmetric';
  }
  return 'corner';
}

type FillParseResult =
  | { kind: 'ok'; color?: string }
  | { kind: 'reject'; reason: VectorImportRejectReason };

/** Parse the `vectorFill` chunk to a hex color string. PSD
 *  `VectorContent` is a discriminated union of color / gradient /
 *  noise-gradient / pattern; only `type: 'color'` is mapped
 *  natively. Absence → no fill (transparent). */
function parsePsdVectorFill(fill: PsdLayer['vectorFill'] | undefined): FillParseResult {
  if (!fill) return { kind: 'ok' };
  if (fill.type === 'color') {
    return { kind: 'ok', color: psdColorToHex(fill.color) };
  }
  if (fill.type === 'pattern') return { kind: 'reject', reason: 'pattern-fill' };
  return { kind: 'reject', reason: 'gradient-fill' };
}

type StrokeParseResult =
  | { kind: 'ok'; stroke: VectorStroke | undefined }
  | { kind: 'reject'; reason: VectorImportRejectReason };

/** Parse the `vectorStroke` chunk into a `VectorStroke`. Absent or
 *  disabled stroke → undefined (no stroke). Gradient strokes
 *  reject (editor's VectorStroke.color is string-only). */
function parsePsdVectorStroke(psdLayer: PsdLayer): StrokeParseResult {
  const stroke = psdLayer.vectorStroke;
  if (!stroke || stroke.strokeEnabled === false) return { kind: 'ok', stroke: undefined };
  // Stroke content lives separately under `vectorStrokeContent`
  // when present; ag-psd doesn't always expose it. Default the
  // stroke color to black when content isn't parseable; the stash
  // restore path preserves the original fidelity.
  const contentRaw = (
    psdLayer as unknown as { vectorStrokeContent?: { type?: string; color?: Color } }
  ).vectorStrokeContent;
  if (contentRaw && contentRaw.type && contentRaw.type !== 'color') {
    return { kind: 'reject', reason: 'unsupported-stroke' };
  }
  const color = contentRaw?.color ? psdColorToHex(contentRaw.color) : '#000000';
  const width =
    stroke.lineWidth && typeof stroke.lineWidth.value === 'number' ? stroke.lineWidth.value : 1;
  const cap = stroke.lineCapType;
  const join = stroke.lineJoinType;
  return {
    kind: 'ok',
    stroke: {
      color,
      width,
      ...(cap ? { cap } : {}),
      ...(join ? { join } : {}),
    },
  };
}

// psdColorToHex imported from psd-color-utils.ts — shared with
// psd-text-import.ts.
